'use client';

import { useState, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { isSameDay, formatISO } from 'date-fns';
import { AutoPopupModal } from '@/components/widgets/modal';
import { LabelCheckButton } from '@/components/check-elements/label-check-button';
import { timezoneService } from '@/services/timezone-service';
import webStorageKeys from '@/constants/web-storage-keys';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { useAuthToken } from '@/store/auth';
import { KafkaNote } from '@nf/utils-common/constants';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { AutoPopupKey } from '@nf/auto-popup-handler';

import { PopupBannerList } from '../popup-banner-list';
import { usePopupBanners } from '@/features/popup-banner/hooks/use-popup-banners';

const getToday = () => timezoneService.clientToTimezone(formatISO(new Date()), -4);

export const LoginBanner = () => {
	const t = useTranslations('General');
	const { enableListPopupBanner } = useLicSettings();
	const banners = usePopupBanners({ isfromLoginBanner: true });
	const [isOpen, setIsOpen] = useState(false);
	const [isSelected, setIsSelected] = useState(false);
	const authToken = useAuthToken();
	const isBefore = !authToken;
	const wasDismissedRef = useRef(false);

	useEffect(() => {
		if (wasDismissedRef.current || isBefore) return;
		const closedData = localStorage.getItem(webStorageKeys.PROMOTION_ACTIVITY_MODAL_CLOSED);
		if (!closedData) {
			setIsOpen(true);
			return;
		}
		const { timestamp } = JSON.parse(closedData);
		if (isSameDay(getToday(), timestamp)) {
			setIsOpen(false);
		} else {
			localStorage.removeItem(webStorageKeys.PROMOTION_ACTIVITY_MODAL_CLOSED);
			setIsOpen(true);
		}
	}, [isBefore]);

	const handleClose = () => {
		setKafkaNormal({
			note: KafkaNote.WcPromotionHub,
			type: 1,
			action: isSelected ? 2 : 1
		});
		if (isSelected) {
			localStorage.setItem(
				webStorageKeys.PROMOTION_ACTIVITY_MODAL_CLOSED,
				JSON.stringify({ timestamp: getToday() })
			);
		}
		wasDismissedRef.current = true;
		setIsOpen(false);
	};

	const handleCheck = (event: React.MouseEvent) => {
		event.preventDefault();
		setIsSelected(!isSelected);
	};

	if (!enableListPopupBanner || isBefore || banners.length === 0) return null;

	return (
		<AutoPopupModal
			popupKey={AutoPopupKey.PopupBannerList}
			isOpen={isOpen}
			closeModal={handleClose}
			variant="center"
			title={t('lbl_PopupHighlight')}
			className="login-banner"
			isBackdropClose={false}
		>
			<PopupBannerList closePopupBanner={() => setIsOpen(false)} isfromLoginBanner={true} />
			<div className="login-banner__checkbox">
				<LabelCheckButton selected={isSelected} onClick={(event) => handleCheck(event)}>
					{t('lbl_TWPopupShowAgain')}
				</LabelCheckButton>
			</div>
		</AutoPopupModal>
	);
};
